import type { ReactNode } from "react";
import { AuthLayout } from "@/layouts/AuthLayout";
import {
  Anchor,
  Box,
  Button,
  Divider,
  Flex,
  rem,
  rgba,
  Stack,
  Text,
  useMantineColorScheme,
} from "@mantine/core";
import { GoogleSignInButton } from "@/features/login/components/GoogleSignInButton";
import { Link } from "react-router-dom";
import { SignUpForm } from "./components/SignUpForm";
import { SignUpHeroPanel } from "./components/SignUpHeroPanel";
import { useSignUpController } from "./hooks/useSignUpController";
import { useSalesEntryOffer } from "./hooks/useSalesEntryOffer";
import { checkPasswordStrength } from "@/shared/utils/password";
import { useMobileScreen } from "@/shared/hooks/useMobileScreen";

/** Selo de oferta do topo no mobile, onde o painel da esquerda não é renderizado. */
function MobileOfferPill({ children }: { children: ReactNode }) {
  return (
    <Box
      px={rem(10)}
      py={rem(4)}
      w="fit-content"
      style={{
        background: "var(--mantine-color-R500-0)",
        color: "white",
        borderRadius: rem(999),
        fontWeight: 800,
        fontSize: rem(11),
        letterSpacing: "0.3px",
      }}>
      {children}
    </Box>
  );
}

export default function SignUpPage() {
  const { colorScheme } = useMantineColorScheme();
  const { isMobileScreen } = useMobileScreen();
  const { form, loading, handleSignUp } = useSignUpController();
  // SA-1218: o link de vendas "pagamento direto" cobra no ato, então esta página
  // não pode prometer teste grátis para quem chegou por ele.
  // SA-1279: e o `?promo=` da mesma URL rende o selo do cupom.
  const { showTrialCopy, promoHeadline } = useSalesEntryOffer();

  const FORM_ID = "sign-up-form";

  const passwordVal = form.values.password;
  const { isValid: isPasswordValid } = checkPasswordStrength(passwordVal);

  const dimmed =
    colorScheme === "light"
      ? rgba("var(--mantine-color-D900-0)", 0.6)
      : rgba("var(--mantine-color-L100-0)", 0.6);

  const SignUpTitle = (
    <Stack
      gap={rem(8)}
      mb="min(max(5%, 12px), 32px)">
      {/* No mobile o painel da esquerda não é renderizado, então os selos da
          oferta aparecem aqui. Cupom e trial são independentes: um link de
          pagamento direto com cupom mostra só o do cupom. */}
      {isMobileScreen && promoHeadline && (
        <MobileOfferPill>🏷️ {promoHeadline}</MobileOfferPill>
      )}
      {isMobileScreen && showTrialCopy && (
        <MobileOfferPill>🎁 7 dias grátis · sem cartão</MobileOfferPill>
      )}
      <Text
        component="h2"
        ff="heading"
        fz={rem(28)}
        fw={400}
        lh={1.1}
        m={0}
        c="var(--mantine-color-plain_text-0)">
        Crie sua conta em segundos
      </Text>
      <Text
        fz={rem(15)}
        c={dimmed}>
        Em poucos minutos seu agente de IA está atendendo no WhatsApp.
      </Text>
    </Stack>
  );

  const SignUpBody = (
    <Flex
      direction="column"
      w="100%"
      gap="md">
      <GoogleSignInButton />
      <Divider
        label="ou continue com seu e-mail"
        labelPosition="center"
        styles={{
          label: {
            fontSize: rem(12),
            color: dimmed,
          },
        }}
      />
      <SignUpForm
        form={form}
        formId={FORM_ID}
        onSubmit={handleSignUp}
      />
    </Flex>
  );

  // Botão de submit ancorado fora da área rolável (slot `actions`), sempre
  // visível — o medidor de força da senha cresce dentro do scroll sem empurrá-lo.
  const SignUpActions = (
    <Button
      fullWidth
      size="sm"
      type="submit"
      form={FORM_ID}
      radius={rem(8)}
      loading={loading}
      title="Preencha os campos corretamente para se cadastrar"
      disabled={!isPasswordValid || !form.isValid() || loading}
      bg="var(--mantine-color-contrast_text-0)"
      ff="heading"
      fz={16}
      fw={700}>
      {/* "grátis" sai no link de pagamento direto: a conta é cobrada minutos
          depois, no gate logo após o wizard. */}
      {showTrialCopy ? "Criar minha conta grátis" : "Criar minha conta"}
    </Button>
  );

  const SignUpFooter = (
    <Text
      ta="center"
      fz={16}
      c={dimmed}>
      Já possui uma conta?{" "}
      <Anchor
        component={Link}
        to="/"
        style={{
          color: "var(--mantine-color-R500-0)",
          fontWeight: 700,
          fontSize: 16,
        }}>
        Fazer login
      </Anchor>
    </Text>
  );

  return (
    <AuthLayout
      title={SignUpTitle}
      footer={SignUpFooter}
      actions={SignUpActions}
      leftPanel={
        <SignUpHeroPanel
          showTrialCopy={showTrialCopy}
          promoHeadline={promoHeadline}
        />
      }
      hideLeftPanelOnMobile>
      {SignUpBody}
    </AuthLayout>
  );
}
